import { FEATURE_FLAG, isValidSubscription } from "@blacknut/javascript-sdk/dist";
import { logE, logD } from "@blacknut/logging/dist";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { getDeviceStreams, streamStopped } from "../redux/actions/Game";
import {
  dismissFamilyInfoLoadError,
  dismissResetPinError,
  loadFamilyInfo,
  resetPin,
  selectProfile,
} from "../redux/actions/Profiles";
import { dismissAuthenticationError, logout } from "../redux/actions/User";
import { State } from "../redux/reducers";
import { usePrevious } from "../utils/Hooks";
import { RouteNames, WithRouterProps } from "../utils/WithRouter";

const TAG = "ProfilePin";
export const useProfileUnlockPage = (
  props: WithRouterProps<{ id: string; returnUrl?: string }> & { returnUrl?: string },
) => {
  const dispatch = useDispatch();
  const pId = props.routeParams && props.routeParams.id;

  const {
    familyProfiles: profilesState,
    profile: authenticatedProfile,
    resetPin: resetPinState,
  } = useSelector((state: State) => state.profilesState);

  const {
    error: profilesLoadError,
    inProgress: profilesLoadInProgress,
    profiles,
  } = profilesState;
  const { error: resetPinError, inProgress: resetPinInProgress } = resetPinState;
  const profile = profiles?.find((p) => p.id === pId);
  const { familyToken, authentication } = useSelector(
    (state: State) => state.globalState,
  );
  const { error: authenticationError, inProgress: authenticating } = authentication;
  const [profileOnMount] = React.useState(authenticatedProfile);

  const { audid, config } = useSelector((state: State) => state.globalState);

  const mounted = React.useRef(false);
  React.useEffect(() => {
    if (familyToken && !profiles && !mounted.current) {
      const init = async () => {
        mounted.current = true;
        try {
          await loadFamilyInfo()(dispatch);
        } catch (e) {
          logE(TAG, "Caught error loading profiles");
        }
      };
      init();
    }
  }, [dispatch, familyToken, profiles, profilesLoadError, profilesLoadInProgress]);

  // see https://blacknut.atlassian.net/browse/CA-1394
  const prevToken = usePrevious(familyToken);
  React.useEffect(() => {
    if (prevToken && !familyToken) {
      props.navigate({ name: RouteNames.SUBSCRIPTION, clearHistory: true });
    }
  }, [prevToken, familyToken, props]);

  const featureFlags = config?.features || {};
  const allowNoSubscription =
    featureFlags[FEATURE_FLAG.ALLOW_NAVIGATION_SUBSCRIPTION_KO];
  return {
    profile,
    resetPinError,
    resetPinInProgress,
    authenticationError,
    authenticating,
    loading: profilesLoadInProgress,
    loadError: profilesLoadError,
    config,
    dismissLoadError: React.useCallback(
      () => dismissFamilyInfoLoadError()(dispatch),
      [dispatch],
    ),
    dismissAuthenticationError: React.useCallback(
      () => dismissAuthenticationError()(dispatch),
      [dispatch],
    ),
    dismissResetPinError: React.useCallback(
      () => dismissResetPinError()(dispatch),
      [dispatch],
    ),
    onSelectProfile: React.useCallback(
      async (pin: string) => {
        let returnUrl = props.returnUrl;
        if (!returnUrl && props.routeParams) {
          returnUrl = props.routeParams.returnUrl;
        }
        if (!profile) {
          return Promise.reject("no input profile");
        }
        if (profile && profile.id === profileOnMount?.id) {
          // When selecting same profile ignore return url and pin lock
          logD(TAG, "Selecting same profile");
          if (returnUrl) {
            props.navigate(returnUrl as string);
          } else {
            props.navigate({ name: RouteNames.HOME, clearHistory: true });
          }
          return Promise.resolve();
        } else {
          try {
            const {
              user,
              profile: authenticatedProfile,
              activeStreams,
              billingInfo,
            } = await selectProfile(profile, pin, true)(dispatch);
            const deviceStreams =
              activeStreams &&
              activeStreams.user &&
              getDeviceStreams(activeStreams.user, audid || "");
            // User selected a profile
            if (returnUrl) {
              // open via deeplink, cancel all active streams if any
              if (deviceStreams && deviceStreams.length > 0) {
                deviceStreams.map((u) => {
                  streamStopped(u.streamId, "Canceled", undefined, false)(dispatch);
                });
              }
              props.navigate(returnUrl as string);
            } else {
              if (
                user &&
                authenticatedProfile.isMaster &&
                !isValidSubscription(user, billingInfo) &&
                !allowNoSubscription
              ) {
                props.navigate({
                  name: RouteNames.PAYMENT_SELECTION,
                });
                return;
              }

              if (deviceStreams && deviceStreams.length > 0) {
                props.navigate({
                  name: RouteNames.STREAM_RECOVERY,
                  clearHistory: true,
                });
              } else {
                props.navigate({ name: RouteNames.HOME, clearHistory: true });
              }
            }
          } catch (e) {
            logE(TAG, "Caught error selecting profile", e);
            throw e;
          }
        }
      },
      [allowNoSubscription, audid, dispatch, profile, profileOnMount?.id, props],
    ),

    resetPin: React.useCallback(async () => {
      dismissAuthenticationError()(dispatch);
      try {
        await resetPin()(dispatch);
      } catch (e) {
        logE(TAG, "Caught error on reset pin", e);
        throw e;
      }
    }, [dispatch]),
    logout: React.useCallback(() => {
      logout()(dispatch);
    }, [dispatch]),
  };
};
